/**
 * Custom React Query Hook
 * 
 * Wrapper around React Query's useQuery that integrates with your existing apiRequest.
 * Provides automatic caching, refetching, and error handling.
 * 
 * @example
 * const { data, isLoading, error, refetch } = useQuery({
 *   queryKey: ['dashboard-metrics'],
 *   endpoint: '/auth/get-dashboard-metrics',
 *   enabled: isAuthenticated, // Only fetch when authenticated
 * });
 */

import { useQuery as useReactQuery } from '@tanstack/react-query';
import { apiRequest } from '@/shared/services/apiClient.js';
import logger from '@/shared/utils/logger.js';

/**
 * Custom useQuery hook that integrates with your apiRequest
 * 
 * @param {Object} options - Query options
 * @param {string|Array} options.queryKey - Unique key for this query (used for caching)
 * @param {string} options.endpoint - API endpoint to call
 * @param {Object} options.apiOptions - Options to pass to apiRequest (method, body, headers, etc.)
 * @param {boolean} options.enabled - Whether the query should run (default: true)
 * @param {number} options.staleTime - Time in ms before data is considered stale (default: 5 minutes)
 * @param {boolean} options.refetchOnWindowFocus - Refetch when window regains focus (default: true)
 * @param {Function} options.onSuccess - Callback when query succeeds
 * @param {Function} options.onError - Callback when query fails
 * @param {Function} options.select - Transform the data before returning
 * @returns {Object} Query result with data, isLoading, error, refetch, etc.
 */
export function useQuery({
  queryKey,
  endpoint,
  queryFn,
  apiOptions = {},
  enabled = true,
  staleTime = 5 * 60 * 1000,
  refetchOnWindowFocus = false,
  refetchOnMount = true,
  retry = 3,
  retryDelay = (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  select,
  onError,   // kept for backward compat but handled inside queryFn (v5 removed it as an option)
  onSuccess, // kept for backward compat but handled inside queryFn (v5 removed it as an option)
  ...otherOptions
}) {
  // Check if logout is in progress - disable query if so
  const isLoggingOut = typeof localStorage !== 'undefined' && localStorage.getItem('isLoggingOut') === 'true';
  
  const isAuthQuery = Array.isArray(queryKey) && queryKey.some(key => 
    typeof key === 'string' && (key.includes('auth') || key.includes('login') || key.includes('token'))
  );
  
  const shouldEnable = enabled && (!isLoggingOut || isAuthQuery);

  return useReactQuery({
    queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
    queryFn: async (context) => {
      // Re-check at execution time
      const stillLoggingOut = typeof localStorage !== 'undefined' && localStorage.getItem('isLoggingOut') === 'true';
      if (stillLoggingOut && !isAuthQuery) {
        logger.warn('[useQuery] isLoggingOut flag is set — skipping request', { endpoint });
        throw new Error('Logout in progress. API calls are disabled.');
      }

      try {
        let result;
        if (queryFn) {
          result = await queryFn(context);
        } else {
          result = await apiRequest(endpoint, {
            method: 'GET',
            ...apiOptions
          });
        }

        if (onSuccess) {
          onSuccess(result);
        }

        return result;
      } catch (error) {
        if (onError) {
          onError(error);
        }
        throw error;
      }
    },
    enabled: shouldEnable,
    staleTime,
    refetchOnWindowFocus: refetchOnWindowFocus && !isLoggingOut,
    refetchOnMount,
    retry,
    retryDelay,
    select,
    // NOTE: do NOT spread otherOptions blindly — unknown keys cause React Query v5 warnings
    // Only spread known-safe options
    ...(otherOptions.refetchInterval !== undefined ? { refetchInterval: otherOptions.refetchInterval } : {}),
    ...(otherOptions.refetchIntervalInBackground !== undefined
      ? { refetchIntervalInBackground: otherOptions.refetchIntervalInBackground }
      : {}),
    ...(otherOptions.gcTime !== undefined ? { gcTime: otherOptions.gcTime } : {}),
    ...(otherOptions.placeholderData !== undefined ? { placeholderData: otherOptions.placeholderData } : {}),
    ...(otherOptions.networkMode !== undefined ? { networkMode: otherOptions.networkMode } : {}),
  });
}

export default useQuery;

